import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';

import type { PaletteMode, Theme, ThemeOptions } from '@mui/material';
import { alpha, createTheme } from '@mui/material';

import { createPartyPalette } from './partyPalette';

const lightShadowSm = '0 1px 2px 0 rgb(0 0 0 / 0.05)';
const lightShadowMd = '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)';
const darkShadowSm = '0 1px 2px 0 rgb(0 0 0 / 0.35)';
const darkShadowMd = '0 8px 18px -8px rgb(0 0 0 / 0.75), 0 4px 8px -4px rgb(0 0 0 / 0.55)';

function getShadowTokens(mode: PaletteMode | undefined) {
  return mode === 'dark'
    ? { sm: darkShadowSm, md: darkShadowMd }
    : { sm: lightShadowSm, md: lightShadowMd };
}

function paletteColorMain(color: unknown): string | undefined {
  return typeof color === 'object' && color && 'main' in color && typeof color.main === 'string'
    ? color.main
    : undefined;
}

const fieldRootStyles = (palette: ThemeOptions['palette']) => ({
  backgroundColor: palette?.background?.default,
  borderRadius: '0.375rem',
  boxShadow: 'none',
  transition: 'box-shadow 150ms ease, border-color 150ms ease',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: palette?.inputBorder,
    transition: 'border-color 150ms ease',
  },
  '&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
    borderColor: paletteColorMain(palette?.primary),
  },
  '&.Mui-focused': {
    // Keep the focus indicator on the notched border so it clears floating labels.
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: paletteColorMain(palette?.primary),
      borderWidth: '2px',
    },
  },
  '&.Mui-error': {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: paletteColorMain(palette?.error),
    },
  },
  '&.Mui-disabled': {
    backgroundColor: palette?.muted,
    boxShadow: 'none',
  },
});

const paletteByMode: Record<'light' | 'dark', ThemeOptions['palette']> = {
  light: {
    contrastThreshold: 4.5,
    mode: 'light',
    background: {
      default: 'hsl(0, 0%, 99%)',
      paper: '#ffffff',
    },
    common: {
      black: '#242424',
      white: '#FFFFFF',
    },
    grey: {
      200: '#E0E0E0',
      400: '#A6A6A6',
      500: '#CECECE',
    },
    primary: {
      main: 'hsl(26, 91%, 56%)',
      dark: 'hsl(26, 91%, 50%)',
      contrastText: 'hsl(204, 11%, 15%)',
    },
    secondary: {
      main: 'hsl(26, 91%, 56%)',
      dark: 'hsl(26, 91%, 50%)',
      contrastText: 'hsl(204, 11%, 15%)',
    },
    text: {
      primary: 'hsl(204, 11%, 32%)',
      secondary: 'hsl(204, 8%, 44%)',
      disabled: '#8C8C8C',
    },
    warning: {
      main: 'hsl(45, 95%, 55%)',
      light: 'hsl(45, 95%, 70%)',
    },
    error: {
      main: 'hsl(0, 85%, 55%)',
      light: 'hsl(0, 80%, 65%)',
    },
    success: {
      main: 'hsl(142, 85%, 45%)',
      light: 'hsl(142, 75%, 65%)',
    },
    surface: '#ffffff',
    surfaceHover: 'hsl(210, 16%, 96%)',
    muted: 'hsl(210, 16%, 96%)',
    inputBorder: 'hsl(214, 20%, 90%)',
    statusCard: {
      background: 'hsl(210, 16%, 96%)',
      border: 'hsl(214, 20%, 92%)',
      text: 'hsl(204, 11%, 32%)',
      muted: 'hsl(204, 8%, 44%)',
    },
    border: 'hsl(214, 20%, 92%)',
    divider: 'hsl(214, 20%, 92%)',
  },
  dark: {
    contrastThreshold: 4.5,
    mode: 'dark',
    background: {
      default: 'hsl(204, 11%, 15%)',
      paper: 'hsl(204, 11%, 18%)',
    },
    common: {
      black: 'hsl(204, 11%, 15%)',
      white: '#FFFFFF',
    },
    grey: {
      200: 'hsl(204, 8%, 70%)',
      400: 'hsl(0, 0%, 65%)',
      500: 'hsl(204, 11%, 25%)',
    },
    primary: {
      main: 'hsl(26, 91%, 56%)',
      dark: 'hsl(26, 91%, 50%)',
      contrastText: 'hsl(204, 11%, 15%)',
    },
    secondary: {
      main: 'hsl(26, 91%, 56%)',
      dark: 'hsl(26, 91%, 50%)',
      contrastText: 'hsl(204, 11%, 15%)',
    },
    text: {
      primary: 'hsl(0, 0%, 95%)',
      secondary: 'hsl(0, 0%, 65%)',
      disabled: 'hsl(0, 0%, 65%)',
    },
    warning: {
      main: 'hsl(45, 95%, 60%)',
      light: 'hsl(45, 95%, 70%)',
    },
    error: {
      main: 'hsl(0, 85%, 60%)',
      light: 'hsl(0, 80%, 65%)',
    },
    success: {
      main: 'hsl(142, 85%, 50%)',
      light: 'hsl(142, 75%, 65%)',
    },
    surface: 'hsl(204, 11%, 18%)',
    surfaceHover: 'hsl(204, 11%, 22%)',
    muted: 'hsl(204, 11%, 20%)',
    inputBorder: 'hsl(204, 11%, 25%)',
    border: 'hsl(204, 11%, 25%)',
    divider: 'hsl(204, 11%, 25%)',
    statusCard: {
      background: 'hsl(218, 23%, 23%)',
      border: 'hsl(43, 96%, 56%)',
      text: 'hsl(43, 96%, 56%)',
      muted: 'hsl(220, 13%, 69%)',
    },
  },
};

const typography: ThemeOptions['typography'] = {
  fontFamily: ['Poppins', 'Manrope', 'Helvetica', 'Arial', 'sans-serif'].join(','),
  fontSize: 14,
  button: { fontWeight: 500 },
};

const componentOverrides = (
  palette: ThemeOptions['palette'],
  shadows: ReturnType<typeof getShadowTokens>
): ThemeOptions['components'] => ({
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: ({ theme }) => ({
        textTransform: 'none',
        fontWeight: 500,
        borderRadius: '0.375rem',
        transition: 'background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease',
        '&.Mui-focusVisible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: '2px',
        },
        '@media (pointer: coarse)': { minHeight: '2.75rem' },
      }),
      sizeSmall: {
        minHeight: '2.25rem',
        padding: '0.375rem 0.75rem',
        '@media (pointer: coarse)': { minHeight: '2.75rem' },
      },
      sizeMedium: {
        minHeight: '2.5rem',
        padding: '0.5rem 1rem',
        '@media (pointer: coarse)': { minHeight: '2.75rem' },
      },
      sizeLarge: { minHeight: '2.75rem', padding: '0.625rem 1.5rem' },
      contained: { boxShadow: shadows.sm, '&:hover': { boxShadow: shadows.md } },
      outlinedPrimary: ({ theme }) => ({
        color: theme.palette.text.primary,
        borderColor: theme.palette.inputBorder,
        backgroundColor: theme.palette.background.default,
        '&:hover': {
          backgroundColor: theme.palette.muted,
          borderColor: alpha(theme.palette.primary.main, 0.5),
        },
      }),
      textPrimary: ({ theme }) => ({
        color: theme.palette.text.primary,
        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
      }),
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.text.secondary,
        borderRadius: '0.375rem',
        transition: 'background-color 150ms ease, color 150ms ease',
        '&:hover': {
          color: theme.palette.text.primary,
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
        },
        '&.Mui-focusVisible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: '2px',
        },
        '@media (pointer: coarse)': { minWidth: '2.75rem', minHeight: '2.75rem' },
      }),
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: '0.375rem',
        textTransform: 'none',
        fontWeight: 500,
        color: theme.palette.text.primary,
        borderColor: theme.palette.border,
        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.05) },
        '&.Mui-selected': {
          color: theme.palette.text.primary,
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          borderColor: alpha(theme.palette.primary.main, 0.6),
          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.15) },
        },
        '&.Mui-focusVisible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: '-2px',
        },
        '@media (pointer: coarse)': { minHeight: '2.75rem' },
      }),
    },
  },
  MuiTab: {
    styleOverrides: {
      textColorPrimary: ({ theme }) => ({
        '&.Mui-selected': { color: theme.palette.text.primary },
      }),
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: ({ theme }) => ({ borderRadius: `${theme.shape.borderRadius}px` }),
    },
  },
  MuiSwitch: {
    styleOverrides: {
      switchBase: ({ theme }) => ({ borderRadius: `${theme.shape.borderRadius}px` }),
      thumb: ({ theme }) => ({ borderRadius: `${theme.shape.borderRadius}px` }),
      track: ({ theme }) => ({ borderRadius: `${theme.shape.borderRadius}px` }),
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        backgroundColor: palette?.surface,
        backgroundImage: palette?.partyBackground
          ? `linear-gradient(135deg, ${palette.surface}, ${palette.surfaceHover})`
          : 'none',
        border: `1px solid ${palette?.border}`,
        borderRadius: '0.5rem',
        boxShadow: shadows.sm,
        transition: 'box-shadow 150ms ease, border-color 150ms ease, background-color 150ms ease',
        '@media (hover: hover)': {
          '&:hover': { boxShadow: shadows.md },
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      icon: {
        fontSize: '1rem',
        color: 'inherit',
      },
    },
  },
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundColor: palette?.background?.default,
      },
      '.MuiPickersOutlinedInput-root': {
        ...fieldRootStyles(palette),
      },
      '.MuiPickersOutlinedInput-notchedOutline': {
        borderColor: palette?.inputBorder,
      },
      '.MuiPickersOutlinedInput-root:hover:not(.Mui-disabled):not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline':
        {
          borderColor: paletteColorMain(palette?.primary),
        },
      '.MuiPickersOutlinedInput-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
        borderColor: paletteColorMain(palette?.primary),
        borderWidth: '2px',
      },
      '.MuiPickersOutlinedInput-root.Mui-error .MuiPickersOutlinedInput-notchedOutline': {
        borderColor: paletteColorMain(palette?.error),
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: palette?.surface,
        color: palette?.text?.primary,
        border: `1px solid ${palette?.border}`,
        borderRadius: '0.375rem',
        boxShadow: shadows.md,
        fontSize: '0.875rem',
        fontWeight: 400,
        padding: '0.375rem 0.75rem',
      },
      arrow: { color: palette?.surface },
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        backgroundColor: palette?.surface,
        backgroundImage: palette?.partyBackground
          ? `linear-gradient(135deg, ${palette.surface}, ${palette.surfaceHover})`
          : 'none',
        border: `1px solid ${palette?.border}`,
        borderRadius: '0.5rem',
        boxShadow: shadows.md,
      },
      list: {
        paddingBlock: '0.25rem',
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        marginInline: '0.25rem',
        borderRadius: '0.375rem',
        transition: 'background-color 150ms ease',
        '@media (pointer: coarse)': { minHeight: '2.75rem' },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: fieldRootStyles(palette),
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundColor: palette?.surface,
        backgroundImage: palette?.partyBackground
          ? `linear-gradient(135deg, ${palette.surface}, ${palette.surfaceHover})`
          : 'none',
        border: `1px solid ${palette?.border}`,
        borderRadius: '0.5rem',
        boxShadow: shadows.sm,
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      paper: {
        backgroundColor: palette?.surface,
        backgroundImage: palette?.partyBackground
          ? `linear-gradient(135deg, ${palette.surface}, ${palette.surfaceHover})`
          : 'none',
        border: `1px solid ${palette?.border}`,
        borderRadius: '0.5rem',
        boxShadow: shadows.md,
      },
      option: {
        marginInline: '0.25rem',
        borderRadius: '0.375rem',
      },
    },
  },
});

export function createSiTheme(mode: PaletteMode = 'light', partyBackground?: string): Theme {
  const palette = partyBackground
    ? createPartyPalette(partyBackground)
    : paletteByMode[mode === 'dark' ? 'dark' : 'light'];
  const shadowTokens = partyBackground
    ? {
        sm: `0 0 12px ${alpha(paletteColorMain(palette?.primary)!, 0.18)}`,
        md: `0 0 28px ${alpha(paletteColorMain(palette?.primary)!, 0.35)}`,
      }
    : getShadowTokens(palette?.mode);
  const shadows = createTheme().shadows.map((shadow, index) => {
    if (partyBackground && index > 0) return index < 4 ? shadowTokens.sm : shadowTokens.md;
    if (index === 1) return shadowTokens.sm;
    if (index === 4) return shadowTokens.md;
    return shadow;
  }) as Theme['shadows'];

  return createTheme({
    palette,
    shape: {
      borderRadius: 8,
    },
    shadows,
    typography,
    components: componentOverrides(palette, shadowTokens),
  });
}
